// SupervisionPOC.js — Documentation Fonctionnelle : POC « Supervision » (module Environnement).
// Aperçu interactif de l'écran réel « Mission Control » : bandeau dégradé + barre d'onglets
// (Vue d'ensemble / Environnements / Agents / Fonctions / Tâches / Relations), Vue d'ensemble avec
// 4 cartes KPI dégradées (barre de progression + détails), cartes Relations / Métriques système, et
// listes d'entités (titre + barre de filtres + bascule grille/liste + cartes à pied d'actions).
// DOUBLÉ d'un guide utilisateur pas-à-pas. Actions simulées localement, aucun appel réseau.
// Source de vérité : automata-studio-frontend [AUT][07]-Supervision.js + automata_environment.
import React, { useState } from 'react';
import {
  Monitor, RefreshCw, Settings, Home, Layers, Server, Code, Briefcase, Network,
  Activity, CheckCircle, XCircle, AlertTriangle, Play, Loader2, Cable, Zap, Clock,
  Info, ArrowRight, Gauge, BellRing, ChevronRight, Edit2, Trash2, List as ListIcon, Grid as GridIcon, Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Couleurs des entités (dégradés de l'écran réel).
const ENTITY = {
  environment: { hex: '#6366f1', gradient: 'from-indigo-500 to-indigo-700' },
  agent: { hex: '#a855f7', gradient: 'from-purple-500 to-purple-700' },
  function: { hex: '#14b8a6', gradient: 'from-teal-500 to-teal-700' },
  task: { hex: '#f97316', gradient: 'from-orange-500 to-orange-700' },
};

// Statuts d'exécution des tâches (libellés + couleurs de l'écran réel).
const STATUS = {
  IN_PROGRESS: { label: 'En cours', color: '#3B82F6', dot: 'bg-blue-500' },
  COMPLETED: { label: 'Terminée', color: '#10B981', dot: 'bg-emerald-500' },
  PENDING: { label: 'En attente', color: '#F59E0B', dot: 'bg-amber-500' },
  FAILED: { label: 'Échec', color: '#EF4444', dot: 'bg-red-500' },
  BLOCKED: { label: 'Bloquée', color: '#EF4444', dot: 'bg-red-500' },
  STOPPED: { label: 'Arrêtée', color: '#6B7280', dot: 'bg-gray-500' },
};
const statusOf = (s) => STATUS[s] || STATUS.PENDING;

// Onglets de navigation (identiques à l'écran réel).
const NAV_TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: Home },
  { id: 'environments', label: 'Environnements', icon: Layers },
  { id: 'agents', label: 'Agents', icon: Server },
  { id: 'functions', label: 'Fonctions', icon: Code },
  { id: 'tasks', label: 'Tâches', icon: Briefcase },
  { id: 'relationships', label: 'Relations', icon: Network },
];

// ---- Données en dur --------------------------------------------------------
const ENVIRONMENTS = [
  { id: 1, appName: 'BGFI-Recette', appType: 'GENERO_GDC', appVersion: 'v3.20', isActive: true, agent: 'Genero GDC', deployed: true, functions: 12 },
  { id: 2, appName: 'Core-Banking-UAT', appType: 'ORACLE_DB', appVersion: 'v19c', isActive: true, agent: 'Oracle Database', deployed: true, functions: 8 },
  { id: 3, appName: 'Batch-Linux-Prod', appType: 'LINUX_OS', appVersion: 'RHEL 8', isActive: false, agent: null, deployed: false, functions: 3 },
  { id: 4, appName: 'Partenaire-PSP', appType: 'REST_API', appVersion: 'v2', isActive: true, agent: 'REST API', deployed: true, functions: 5 },
];

const AGENTS = [
  { id: 1, agentType: 'Genero GDC', icon: 'Settings', ipAddress: '10.0.8.70', port: 6400, isActive: true, installationPath: '/opt/automata/agent', environment: 'BGFI-Recette' },
  { id: 2, agentType: 'Oracle Database', icon: 'Database', ipAddress: '10.20.3.10', port: 1521, isActive: true, installationPath: '/opt/automata/db-agent', environment: 'Core-Banking-UAT' },
  { id: 3, agentType: 'Linux OS', icon: 'Terminal', ipAddress: '10.20.5.40', port: 22, isActive: false, installationPath: '/home/automata', environment: null },
  { id: 4, agentType: 'REST API', icon: 'Globe', ipAddress: '10.20.7.80', port: 8443, isActive: true, installationPath: 'n/a', environment: 'Partenaire-PSP' },
];

const FUNCTIONS = [
  { id: 1, functionName: 'Virement SYSTAC2', functionType: 'TRANSACTION', functionDomain: 'PAIEMENT', isActive: true, lastExecution: 'il y a 4 min' },
  { id: 2, functionName: 'Contrôle de solde', functionType: 'REQUETE', functionDomain: 'COMPTE', isActive: true, lastExecution: 'il y a 6 min' },
  { id: 3, functionName: 'Création bénéficiaire', functionType: 'API', functionDomain: 'REFERENTIEL', isActive: true, lastExecution: 'il y a 8 min' },
  { id: 4, functionName: 'Purge des logs', functionType: 'BATCH', functionDomain: 'EXPLOITATION', isActive: false, lastExecution: 'Jamais' },
];

const TASKS = [
  { id: 101, taskName: 'Virement SYSTAC2 — happy path', taskType: 'NON_REGRESSION', executionStatus: 'IN_PROGRESS', function: 'Virement SYSTAC2', progress: 62, createdDate: 'il y a 3 min' },
  { id: 102, taskName: 'Contrôle solde compte Oracle', taskType: 'ACCEPTABILITY', executionStatus: 'COMPLETED', function: 'Contrôle de solde', progress: 100, createdDate: 'il y a 6 min' },
  { id: 103, taskName: 'Appel API création bénéficiaire', taskType: 'END_TO_END', executionStatus: 'FAILED', function: 'Création bénéficiaire', progress: 100, createdDate: 'il y a 8 min' },
  { id: 104, taskName: 'Purge logs serveur Linux', taskType: 'CORRECTIVE', executionStatus: 'PENDING', function: 'Purge des logs', progress: 0, createdDate: 'il y a 1 min' },
  { id: 105, taskName: 'Rejeu campagne nocturne CBPR+', taskType: 'NON_REGRESSION', executionStatus: 'IN_PROGRESS', function: 'Virement SYSTAC2', progress: 28, createdDate: 'à l\'instant' },
];

// ---- Sous-composants de la réplique ----------------------------------------
const ActiveBadge = ({ active, label }) =>
  active
    ? <Badge color="#10B981"><span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />{label || 'Actif'}</Badge>
    : <Badge><span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400" />{label || 'Inactif'}</Badge>;

const StatusBadge = ({ status }) => {
  const s = statusOf(status);
  return <Badge color={s.color}><span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}</Badge>;
};

// Carte KPI dégradée (chiffre + libellé + icône + barre de progression + détails + lien Détails).
const KpiCard = ({ icon: IconCmp, total, label, gradient, progress, details, onClick }) => (
  <button onClick={onClick} className={`text-left bg-gradient-to-br ${gradient} rounded-2xl p-5 text-white shadow-lg transition-transform hover:-translate-y-0.5`}>
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-3xl font-bold leading-none">{total}</p>
        <p className="text-sm text-white/80 mt-1">{label}</p>
      </div>
      <span className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><IconCmp className="h-7 w-7" /></span>
    </div>
    <div className="h-2 rounded-full bg-white/20 mb-2 overflow-hidden">
      <div className="h-full rounded-full bg-white/60 transition-all" style={{ width: `${progress}%` }} />
    </div>
    <div className="flex justify-between text-xs text-white/70">{details.map((d, i) => <span key={i}>{d}</span>)}</div>
    <div className="flex items-center gap-1 mt-3 text-xs text-white/80"><span>Détails</span><ChevronRight className="h-3.5 w-3.5" /></div>
  </button>
);

// Carte de panneau de la Vue d'ensemble (en-tête + corps).
const PanelCard = ({ title, subtitle, right, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// Barre de filtres d'une liste d'entités (recherche + bascule grille/liste + actualiser).
const EntityToolbar = ({ search, onSearch, placeholder, view, onView, onRefresh }) => (
  <div className="flex flex-wrap items-center gap-3 mb-4">
    <SearchBar value={search} onChange={onSearch} placeholder={placeholder} className="flex-1 min-w-[200px] max-w-sm" />
    <div className="flex items-center gap-1 ml-auto">
      <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
        {[{ key: 'grid', icon: GridIcon }, { key: 'list', icon: ListIcon }].map((v) => (
          <button key={v.key} onClick={() => onView(v.key)}
            className={`p-2 rounded-lg transition-all ${view === v.key ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
            <v.icon className="h-4.5 w-4.5" />
          </button>
        ))}
      </div>
      <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><RefreshCw className="h-4.5 w-4.5" /></button>
    </div>
  </div>
);

// Carte d'entité générique (icône dégradée + titre + sous-titre + badge + corps + pied d'actions).
const EntityCard = ({ gradient, icon: IconCmp, iconName, iconColor, title, subtitle, badge, children, actions }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    <div className="p-4">
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${gradient ? `bg-gradient-to-br ${gradient}` : ''}`} style={iconColor ? { backgroundColor: iconColor } : undefined}>
          {iconName ? <Icon name={iconName} className="h-4.5 w-4.5 text-white" /> : <IconCmp className="h-4.5 w-4.5 text-white" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{title}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
        </div>
        {badge}
      </div>
      {children}
    </div>
    <div className="flex border-t border-gray-100 dark:border-gray-700/60">{actions}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const SupervisionPOC = () => {
  const [tab, setTab] = useState('overview');
  const [environments] = useState(ENVIRONMENTS);
  const [agents] = useState(AGENTS);
  const [functions] = useState(FUNCTIONS);
  const [tasks, setTasks] = useState(TASKS);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('grid'); // 'grid' | 'list'
  const [refreshing, setRefreshing] = useState(false);
  const [networkOn, setNetworkOn] = useState(false);
  const [tested, setTested] = useState({}); // { [agentId]: 'testing'|'ok'|'ko' }
  const [toast, setToast] = useState('');

  // Indicateurs KPI dérivés des données en dur.
  const stats = {
    env: { total: environments.length, active: environments.filter((e) => e.isActive).length, withAgent: environments.filter((e) => e.agent).length },
    agent: { total: agents.length, active: agents.filter((a) => a.isActive).length },
    func: { total: functions.length, active: functions.filter((f) => f.isActive).length, domains: new Set(functions.map((f) => f.functionDomain)).size },
    task: {
      total: tasks.length,
      inProgress: tasks.filter((t) => t.executionStatus === 'IN_PROGRESS').length,
      completed: tasks.filter((t) => t.executionStatus === 'COMPLETED').length,
      failed: tasks.filter((t) => t.executionStatus === 'FAILED' || t.executionStatus === 'BLOCKED').length,
    },
  };

  const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

  const showToast = (m) => { setToast(m); window.setTimeout(() => setToast(''), 1800); };

  // Rafraîchissement SIMULÉ : avance la progression des tâches en cours.
  const refreshAll = () => {
    setRefreshing(true);
    window.setTimeout(() => {
      setTasks((prev) => prev.map((t) => {
        if (t.executionStatus !== 'IN_PROGRESS') return t;
        const next = Math.min(100, t.progress + 18);
        return next >= 100 ? { ...t, progress: 100, executionStatus: 'COMPLETED' } : { ...t, progress: next };
      }));
      setRefreshing(false);
      showToast('Données actualisées avec succès');
    }, 700);
  };

  // Test de connectivité SIMULÉ : agent en ligne -> ok, hors ligne -> ko.
  const testAgent = (a) => {
    setTested((t) => ({ ...t, [a.id]: 'testing' }));
    window.setTimeout(() => setTested((t) => ({ ...t, [a.id]: a.isActive ? 'ok' : 'ko' })), 600);
  };

  const q = search.toLowerCase();
  const fEnvs = environments.filter((e) => `${e.appName} ${e.appType} ${e.agent || ''}`.toLowerCase().includes(q));
  const fAgents = agents.filter((a) => `${a.agentType} ${a.ipAddress} ${a.environment || ''}`.toLowerCase().includes(q));
  const fFuncs = functions.filter((f) => `${f.functionName} ${f.functionType} ${f.functionDomain}`.toLowerCase().includes(q));
  const fTasks = tasks.filter((t) => `${t.taskName} ${t.taskType} ${t.function}`.toLowerCase().includes(q));

  const changeTab = (id) => { setTab(id); setSearch(''); setView('grid'); };

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation Fonctionnelle — Supervision</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Mission Control</strong> (Supervision) (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== (2) Aperçu interactif de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* Bandeau dégradé (titre Mission Control + boutons Actualiser / Réglages) */}
        <div className="m-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-700 p-6 shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Monitor className="h-5.5 w-5.5 text-white" /></span>
              <div>
                <h2 className="text-2xl font-bold text-white leading-tight">Mission Control</h2>
                <p className="text-blue-100 text-sm">Supervision globale des environnements et agents</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={refreshAll} disabled={refreshing} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-60">
                <RefreshCw className={`h-4.5 w-4.5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"><Settings className="h-4.5 w-4.5" /></button>
            </div>
          </div>
        </div>

        {/* Barre d'onglets de navigation */}
        <div className="flex items-center gap-1 px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {NAV_TABS.map((t) => {
            const TIcon = t.icon;
            const on = tab === t.id;
            return (
              <button key={t.id} onClick={() => changeTab(t.id)}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${on ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}>
                <TIcon className="h-4 w-4" />{t.label}
                {on && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {/* ----- Onglet : Vue d'ensemble ----- */}
          {tab === 'overview' && (
            <div className="space-y-4">
              {/* 4 cartes KPI dégradées */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiCard icon={Layers} total={stats.env.total} label="Environnements" gradient={ENTITY.environment.gradient}
                  progress={pct(stats.env.active, stats.env.total)} details={[`${stats.env.active} actifs`, `${stats.env.withAgent} avec agent`]} onClick={() => changeTab('environments')} />
                <KpiCard icon={Server} total={stats.agent.total} label="Agents" gradient={ENTITY.agent.gradient}
                  progress={pct(stats.agent.active, stats.agent.total)} details={[`${stats.agent.active} actifs`, `${stats.agent.total - stats.agent.active} hors ligne`]} onClick={() => changeTab('agents')} />
                <KpiCard icon={Code} total={stats.func.total} label="Fonctions" gradient={ENTITY.function.gradient}
                  progress={pct(stats.func.active, stats.func.total)} details={[`${stats.func.active} actives`, `${stats.func.domains} domaines`]} onClick={() => changeTab('functions')} />
                <KpiCard icon={Briefcase} total={stats.task.total} label="Tâches" gradient={ENTITY.task.gradient}
                  progress={pct(stats.task.completed, stats.task.total)} details={[`${stats.task.completed} terminées`, `${stats.task.inProgress} en cours`, `${stats.task.failed} échecs`]} onClick={() => changeTab('tasks')} />
              </div>

              {/* Relations + Métriques système */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PanelCard title="Relations entre entités"
                  right={<button onClick={() => setNetworkOn((v) => !v)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><Network className="h-4.5 w-4.5" /></button>}>
                  <div className="h-[260px] flex flex-col items-center justify-center gap-3 p-4">
                    {networkOn ? (
                      <>
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          {[['Environnements', ENTITY.environment.hex], ['Agents', ENTITY.agent.hex], ['Fonctions', ENTITY.function.hex], ['Tâches', ENTITY.task.hex]].map(([l, c]) => (
                            <Badge key={l} color={c}><span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />{l}</Badge>
                          ))}
                        </div>
                        <Network className="h-12 w-12 text-blue-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Graphe de relations (démo) — entités reliées par environnement / fonction / tâche.</p>
                      </>
                    ) : (
                      <>
                        <Network className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cliquez pour activer la vue réseau</p>
                        <Button variant="outline" size="sm" onClick={() => setNetworkOn(true)}><Network className="h-4 w-4" />Activer la vue réseau</Button>
                      </>
                    )}
                  </div>
                </PanelCard>

                <PanelCard title="Métriques système">
                  <div className="h-[260px] flex flex-col items-center justify-center gap-4 p-4 text-center">
                    <Gauge className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Métriques disponibles dans le module Performance</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">CPU, mémoire et réseau en temps réel.</p>
                    </div>
                    <Button onClick={() => showToast('Redirection (démo) vers le module Performance')}><Gauge className="h-4 w-4" />Module Performance</Button>
                  </div>
                </PanelCard>
              </div>

              {/* Cartes graphiques (distribution + alertes) */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <PanelCard title="Distribution des environnements" subtitle="Par type d'application">
                  <div className="p-4 space-y-2">
                    {Object.entries(environments.reduce((a, e) => { a[e.appType] = (a[e.appType] || 0) + 1; return a; }, {})).map(([name, v], i) => (
                      <div key={name} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 dark:text-gray-300 w-28 truncate">{name}</span>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct(v, stats.env.total)}%`, backgroundColor: ['#6366f1', '#06b6d4', '#22c55e', '#f59e0b'][i % 4] }} />
                        </div>
                        <span className="text-xs text-gray-500 w-5 text-right">{v}</span>
                      </div>
                    ))}
                  </div>
                </PanelCard>

                <PanelCard title="Types d'agents" subtitle="Distribution par type">
                  <div className="p-4 space-y-2">
                    {agents.map((a, i) => (
                      <div key={a.id} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 dark:text-gray-300 w-28 truncate">{a.agentType}</span>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: '100%', backgroundColor: ['#a855f7', '#7c3aed', '#14b8a6', '#f97316'][i % 4] }} />
                        </div>
                        <span className="text-xs text-gray-500 w-5 text-right">1</span>
                      </div>
                    ))}
                  </div>
                </PanelCard>

                <PanelCard title="Statuts des tâches" subtitle="Distribution par statut">
                  <div className="p-4 space-y-2">
                    {[['En attente', tasks.filter((t) => t.executionStatus === 'PENDING').length, '#F59E0B'], ['En cours', stats.task.inProgress, '#3B82F6'], ['Terminées', stats.task.completed, '#10B981'], ['Échecs', stats.task.failed, '#EF4444']].map(([l, v, c]) => (
                      <div key={l} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 dark:text-gray-300 w-24 truncate">{l}</span>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct(v, stats.task.total)}%`, backgroundColor: c }} />
                        </div>
                        <span className="text-xs text-gray-500 w-5 text-right">{v}</span>
                      </div>
                    ))}
                  </div>
                </PanelCard>

                <PanelCard title="Alertes système" right={<BellRing className="h-4 w-4 text-gray-400" />}>
                  <div className="h-[150px] flex flex-col items-center justify-center gap-2 p-4">
                    <BellRing className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Aucune alerte système</p>
                  </div>
                </PanelCard>
              </div>
            </div>
          )}

          {/* ----- Onglet : Environnements ----- */}
          {tab === 'environments' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Environnements ({fEnvs.length})</h2>
              <EntityToolbar search={search} onSearch={setSearch} placeholder="Rechercher un environnement…" view={view} onView={setView} onRefresh={refreshAll} />
              {fEnvs.length === 0 ? (
                <EmptyState icon={Layers} title="Aucun environnement" message="Aucun environnement ne correspond à la recherche." />
              ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {fEnvs.map((env) => (
                    <EntityCard key={env.id} gradient={ENTITY.environment.gradient} icon={Layers} title={env.appName} subtitle={env.appVersion} badge={<ActiveBadge active={env.isActive} />}
                      actions={<>
                        <button onClick={() => showToast(`Modifier « ${env.appName} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30"><Edit2 className="h-3.5 w-3.5" />Modifier</button>
                        <div className="w-px bg-gray-100 dark:bg-gray-700/60" />
                        <button onClick={() => showToast(`Supprimer « ${env.appName} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10"><Trash2 className="h-3.5 w-3.5" />Supprimer</button>
                      </>}>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        <Badge color="#6366f1">{env.appType}</Badge>
                        {env.agent && <Badge color="#a855f7"><Server className="h-3 w-3" />{env.agent}</Badge>}
                        {env.deployed && <Badge color="#3B82F6"><Zap className="h-3 w-3" />Déployé</Badge>}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{env.functions} fonctions associées</p>
                    </EntityCard>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">{['ID', 'Nom', 'Type', 'Version', 'Agent', 'Statut'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                      {fEnvs.map((env) => (
                        <tr key={env.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                          <td className="px-4 py-3 text-gray-500">{env.id}</td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{env.appName}</td>
                          <td className="px-4 py-3"><Badge color="#6366f1">{env.appType}</Badge></td>
                          <td className="px-4 py-3 text-gray-500">{env.appVersion}</td>
                          <td className="px-4 py-3">{env.agent ? <Badge color="#a855f7"><Server className="h-3 w-3" />{env.agent}</Badge> : <span className="text-xs text-gray-400">Aucun</span>}</td>
                          <td className="px-4 py-3"><ActiveBadge active={env.isActive} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ----- Onglet : Agents ----- */}
          {tab === 'agents' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Agents ({fAgents.length})</h2>
              <EntityToolbar search={search} onSearch={setSearch} placeholder="Rechercher un agent…" view={view} onView={setView} onRefresh={refreshAll} />
              {fAgents.length === 0 ? (
                <EmptyState icon={Server} title="Aucun agent" message="Aucun agent ne correspond à la recherche." />
              ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {fAgents.map((a) => {
                    const t = tested[a.id];
                    return (
                      <EntityCard key={a.id} iconName={a.icon} iconColor={ENTITY.agent.hex} title={`Agent ${a.id}`} subtitle={a.agentType} badge={<ActiveBadge active={a.isActive} />}
                        actions={<>
                          <button onClick={() => showToast(`Modifier « Agent ${a.id} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30"><Edit2 className="h-3.5 w-3.5" />Modifier</button>
                          <div className="w-px bg-gray-100 dark:bg-gray-700/60" />
                          <button onClick={() => testAgent(a)} disabled={t === 'testing'} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10 disabled:opacity-60">{t === 'testing' ? 'Test…' : <><Cable className="h-3.5 w-3.5" />Tester</>}</button>
                        </>}>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div><p className="text-[10px] uppercase tracking-wider text-gray-400">Adresse IP</p><p className="text-sm text-gray-900 dark:text-white">{a.ipAddress}</p></div>
                          <div><p className="text-[10px] uppercase tracking-wider text-gray-400">Port</p><p className="text-sm text-gray-900 dark:text-white">{a.port}</p></div>
                        </div>
                        <div><p className="text-[10px] uppercase tracking-wider text-gray-400">Environnement</p><p className="text-sm text-gray-900 dark:text-white">{a.environment || 'Aucun'}</p></div>
                        {t === 'ok' && <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300"><CheckCircle className="h-3.5 w-3.5" />Agent connecté</p>}
                        {t === 'ko' && <p className="mt-2 flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300"><XCircle className="h-3.5 w-3.5" />Échec connexion (hors ligne)</p>}
                      </EntityCard>
                    );
                  })}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">{['ID', 'Type', 'Adresse IP', 'Port', 'Statut', 'Environnement'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                      {fAgents.map((a) => (
                        <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                          <td className="px-4 py-3 text-gray-500">{a.id}</td>
                          <td className="px-4 py-3"><Badge color="#a855f7"><Server className="h-3 w-3" />{a.agentType}</Badge></td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{a.ipAddress}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{a.port}</td>
                          <td className="px-4 py-3"><ActiveBadge active={a.isActive} /></td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{a.environment || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ----- Onglet : Fonctions ----- */}
          {tab === 'functions' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Fonctions ({fFuncs.length})</h2>
              <EntityToolbar search={search} onSearch={setSearch} placeholder="Rechercher une fonction…" view={view} onView={setView} onRefresh={refreshAll} />
              {fFuncs.length === 0 ? (
                <EmptyState icon={Code} title="Aucune fonction" message="Aucune fonction ne correspond à la recherche." />
              ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {fFuncs.map((f) => (
                    <EntityCard key={f.id} gradient={ENTITY.function.gradient} icon={Code} title={f.functionName} subtitle={f.functionType} badge={<ActiveBadge active={f.isActive} label={f.isActive ? 'Active' : 'Inactive'} />}
                      actions={<>
                        <button onClick={() => showToast(`Modifier « ${f.functionName} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30"><Edit2 className="h-3.5 w-3.5" />Modifier</button>
                        <div className="w-px bg-gray-100 dark:bg-gray-700/60" />
                        <button onClick={() => showToast(`Supprimer « ${f.functionName} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10"><Trash2 className="h-3.5 w-3.5" />Supprimer</button>
                      </>}>
                      <div className="flex flex-wrap gap-1.5 mb-3"><Badge color="#14b8a6">{f.functionDomain}</Badge></div>
                      <p className="text-xs text-gray-400">Dernière exécution : {f.lastExecution}</p>
                    </EntityCard>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">{['ID', 'Nom', 'Type', 'Domaine', 'Statut', 'Dernière exécution'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                      {fFuncs.map((f) => (
                        <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                          <td className="px-4 py-3 text-gray-500">{f.id}</td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{f.functionName}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{f.functionType}</td>
                          <td className="px-4 py-3"><Badge color="#14b8a6">{f.functionDomain}</Badge></td>
                          <td className="px-4 py-3"><ActiveBadge active={f.isActive} label={f.isActive ? 'Active' : 'Inactive'} /></td>
                          <td className="px-4 py-3 text-xs text-gray-500">{f.lastExecution}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ----- Onglet : Tâches ----- */}
          {tab === 'tasks' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tâches ({fTasks.length})</h2>
              <EntityToolbar search={search} onSearch={setSearch} placeholder="Rechercher une tâche…" view={view} onView={setView} onRefresh={refreshAll} />
              {fTasks.length === 0 ? (
                <EmptyState icon={Briefcase} title="Aucune tâche" message="Aucune tâche ne correspond à la recherche." />
              ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {fTasks.map((t) => (
                    <EntityCard key={t.id} gradient={ENTITY.task.gradient} icon={Briefcase} title={t.taskName} subtitle={t.taskType} badge={<StatusBadge status={t.executionStatus} />}
                      actions={<>
                        <button onClick={() => showToast(`Modifier « ${t.taskName} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30"><Edit2 className="h-3.5 w-3.5" />Modifier</button>
                        <div className="w-px bg-gray-100 dark:bg-gray-700/60" />
                        <button onClick={() => showToast(`Exécuter « ${t.taskName} » (démo)`)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10"><Play className="h-3.5 w-3.5" />Exécuter</button>
                      </>}>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        <Badge color="#14b8a6"><Code className="h-3 w-3" />{t.function}</Badge>
                        {t.executionStatus === 'IN_PROGRESS' && <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin self-center" />}
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden mb-2">
                        <div className="h-full rounded-full transition-all" style={{ width: `${t.progress}%`, backgroundColor: statusOf(t.executionStatus).color }} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400"><span>{t.progress}%</span><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{t.createdDate}</span></div>
                    </EntityCard>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">{['ID', 'Nom', 'Type', 'Fonction', 'Statut', 'Avancement'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                      {fTasks.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                          <td className="px-4 py-3 text-gray-500">{t.id}</td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{t.taskName}</td>
                          <td className="px-4 py-3"><Badge>{t.taskType}</Badge></td>
                          <td className="px-4 py-3"><Badge color="#14b8a6"><Code className="h-3 w-3" />{t.function}</Badge></td>
                          <td className="px-4 py-3"><StatusBadge status={t.executionStatus} /></td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{t.progress}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ----- Onglet : Relations ----- */}
          {tab === 'relationships' && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 h-[420px] flex flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {[['Environnements', ENTITY.environment.hex], ['Agents', ENTITY.agent.hex], ['Fonctions', ENTITY.function.hex], ['Tâches', ENTITY.task.hex]].map(([l, c]) => (
                  <Badge key={l} color={c}><span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />{l}</Badge>
                ))}
              </div>
              <Network className="h-16 w-16 text-blue-400" />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Graphe de relations entre entités (démo)</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Environnement → Agent, Environnement → Fonction, Fonction → Tâche. Vue interactive (zoom / déplacement) dans l'écran réel.</p>
              </div>
            </div>
          )}
        </div>

        {/* Notification simulée */}
        {toast && (
          <div className="px-5 pb-4">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircle className="h-4 w-4 shrink-0" />{toast}
            </div>
          </div>
        )}
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Home className="h-5 w-5 text-blue-500" />1. Lire la vue d'ensemble</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir l'onglet <strong>Vue d'ensemble</strong>. <button onClick={() => changeTab('overview')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Lire les 4 <strong>cartes KPI</strong> (environnements, agents, fonctions, tâches) et leur barre de progression.</GuideStep>
              <GuideStep n={3}>Cliquer une carte KPI pour <strong>basculer sur la liste</strong> correspondante.</GuideStep>
              <GuideStep n={4}>Cliquer <strong>Actualiser</strong> (bandeau) pour recharger KPI et listes. <button onClick={refreshAll} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Server className="h-5 w-5 text-blue-500" />2. Surveiller les agents</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir l'onglet <strong>Agents</strong>. <button onClick={() => changeTab('agents')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Vérifier le badge <span className="text-emerald-600">Actif</span> / <span className="text-gray-500">Inactif</span> de chaque agent.</GuideStep>
              <GuideStep n={3}>Basculer entre vue <strong>grille</strong> et <strong>liste</strong>, et filtrer via la recherche.</GuideStep>
              <GuideStep n={4}>Cliquer <strong>Tester</strong> pour contrôler la connectivité d'un agent.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-blue-500" />3. Suivre & réagir aux tâches</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir l'onglet <strong>Tâches</strong>. <button onClick={() => changeTab('tasks')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Repérer les tâches <strong>En cours</strong> (icône qui tourne + barre de progression).</GuideStep>
              <GuideStep n={3}>Isoler les <span className="text-red-600">échecs</span> via la recherche. <button onClick={() => { changeTab('tasks'); setSearch('bénéficiaire'); }} className="text-blue-600 dark:text-blue-400 underline">Voir</button></GuideStep>
              <GuideStep n={4}>Tester l'agent concerné puis <strong>Exécuter</strong> à nouveau la tâche.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Référence : indicateurs supervisés ===== */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Gauge className="h-5 w-5 text-blue-500" />Indicateurs supervisés</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Indicateur', 'Source', 'Description'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {[
                  ['Environnements', 'Environnements', 'Total configuré, dont actifs et environnements disposant d\'un agent'],
                  ['Agents', 'Agents', 'Total d\'agents, dont actifs (en ligne) et hors ligne'],
                  ['Fonctions', 'Fonctions', 'Total de fonctions, dont actives, réparties par domaine'],
                  ['Tâches', 'Tâches', 'Total de tâches, réparties par statut (terminées / en cours / échecs)'],
                  ['Connectivité agent', 'Agents', 'Résultat du test de connexion (Actif / Inactif)'],
                  ['Avancement', 'Tâches', 'Progression de l\'exécution en cours (0–100 %)'],
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-medium">{r[0]}</td>
                    <td className="px-4 py-2"><Badge color="#3B82F6">{r[1]}</Badge></td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ===== (4) Référence : statuts d'exécution ===== */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Network className="h-5 w-5 text-blue-500" />Statuts d'exécution supervisés</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.values(STATUS).map((s) => (
              <Badge key={s.label} color={s.color}><span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ===== (4) Référence : règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><BellRing className="h-5 w-5 text-blue-500" />Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />La supervision est en <strong>lecture seule</strong> : elle agrège l'état des environnements, agents, fonctions et tâches sans les modifier.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>rafraîchissement</strong> recharge les KPI et toutes les listes (manuel ou périodique).</li>
            <li className="flex items-start gap-2"><Zap className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />Une exécution exige un agent <strong>actif et déployé</strong> sur l'environnement cible.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un agent <strong>hors ligne</strong> fait échouer ou bloque les tâches qui en dépendent.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Info className="h-5 w-5 text-blue-500" />Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/environments'],
              ['GET', '#10B981', '/api/automata/agents'],
              ['GET', '#10B981', '/api/automata/functions'],
              ['GET', '#10B981', '/api/automata/tasks'],
              ['GET', '#10B981', '/api/automata/agents/{id}/test-connection'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment (Supervision / Mission Control)</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default SupervisionPOC;
